import React, { useState } from 'react'
import PropTypes from 'prop-types'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { isEmpty, last } from 'lodash'
import {
  Eye as EyeIcon,
  File as FileIcon,
  FileText as FileTextIcon,
  Trash2 as Trash2Icon,
} from 'react-feather'
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core'

import { AlertDialog } from 'components'
import helpers from 'helpers'

import usePermissions from 'hooks/usePermissions'
import useSnackbar from 'hooks/useSnackbar'

import theme from 'theme'
import useStyles from './styles'
import constants from 'constants/index'

const LinkedDocumentList = ({
  items = [],
  onRemove,
  disabledInputs = false,
  renderItemActions,
  emptyMessage = 'Nenhum documento foi encontrado.',
  requiresConfirmation = () => true,
  showDocumentId = false,
  showPendingLabel = false,
}) => {
  const { PRIVACY_POLICIES } = constants.permissions

  const classes = useStyles()
  const { permitted } = usePermissions()
  const snackbar = useSnackbar()

  const [pendingRemoveItem, setPendingRemoveItem] = useState(null)

  const hasDocumentPermissions =
    permitted(PRIVACY_POLICIES.CREATE) || permitted(PRIVACY_POLICIES.LIST)

  const handleConfirmRemove = () => {
    if (onRemove) onRemove(pendingRemoveItem)
    setPendingRemoveItem(null)
  }

  const handleOpenAttachment = (item) => {
    try {
      helpers.file.handleOpen(item)
    } catch {
      snackbar.open({
        message: 'Não foi possível abrir o documento.',
        variant: 'error',
      })
    }
  }

  return (
    <Box>
      <Paper variant="outlined">
        <PerfectScrollbar
          options={{
            wheelPropagation: true,
            useBothWheelAxes: false,
          }}
          className={classes.attachmentItems}
        >
          {isEmpty(items) && (
            <Box px={2} py={1}>
              <Typography variant="overline">{emptyMessage}</Typography>
            </Box>
          )}
          {items?.map((item, index, arr) => {
            const isDocument = item._type === 'document'
            // O documento pode estar em duas formas: já persistido, com os dados na `documentVersion`,
            // ou recém-escolhido no modal, com os dados na raiz do item. Normalizar aqui é o que faz as
            // duas aparecerem iguais na lista.
            const documentVersion =
              item?.documentVersion?.version || item?.version
            const documentReview = item?.documentVersion?.review ?? item?.review
            const privacyPolicyId =
              item?.documentVersion?.privacyPolicyId || item?.id
            // Documento sem `documentVersion` e arquivo sem `id` só existem no formulário: o vínculo é
            // gravado no submit da tela.
            const isPending = isDocument
              ? !item?.documentVersion?.id
              : !item?.id

            return (
              <Box key={item?.id ?? item?.documentVersion?.id ?? index}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gridGap={theme.spacing(2)}
                  px={2}
                  py={1}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gridGap={theme.spacing(2)}
                    className={classes.itemInfo}
                  >
                    <Box className={classes.itemIcon}>
                      {isDocument ? (
                        <FileTextIcon size={20} />
                      ) : (
                        <FileIcon size={20} />
                      )}
                    </Box>
                    <Typography className={classes.itemName}>
                      {item?.name || item?.documentVersion?.title}&nbsp;
                      {!!documentVersion && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="secondary"
                        >
                          v{documentVersion}.{documentReview || 0}
                        </Typography>
                      )}
                      {/* O identificador é o mesmo campo que filtra a listagem de Documentos, então
                          exibi-lo aqui permite localizar o documento lá sem depender do título. */}
                      {showDocumentId && isDocument && !!privacyPolicyId && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="secondary"
                        >
                          &nbsp;&middot; ID {privacyPolicyId}
                        </Typography>
                      )}
                    </Typography>
                    {showPendingLabel && isPending && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="Não salvo"
                        className={classes.pendingChip}
                      />
                    )}
                  </Box>
                  <Box display="flex" className={classes.itemActions}>
                    {renderItemActions?.(item)}

                    {((isDocument && !!item?.export?.url) || !isDocument) && (
                      <Box>
                        <IconButton
                          variant="contained"
                          onClick={() =>
                            isDocument
                              ? handleOpenAttachment(item.export)
                              : handleOpenAttachment(item)
                          }
                        >
                          <EyeIcon size={20} />
                        </IconButton>
                      </Box>
                    )}
                    {!disabledInputs && (
                      <Tooltip
                        title={
                          hasDocumentPermissions
                            ? ''
                            : 'Você não possui permissões de documentos da plataforma'
                        }
                      >
                        <Box>
                          <IconButton
                            disabled={isDocument && !hasDocumentPermissions}
                            onClick={() =>
                              requiresConfirmation(item)
                                ? setPendingRemoveItem(item)
                                : onRemove && onRemove(item)
                            }
                          >
                            <Trash2Icon size={20} />
                          </IconButton>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                {item !== last(arr) && <Divider />}
              </Box>
            )
          })}
        </PerfectScrollbar>
      </Paper>
      <AlertDialog
        open={!!pendingRemoveItem}
        onClose={() => setPendingRemoveItem(null)}
        variant="standard"
        severity="warning"
        content="Tem certeza de que deseja excluir este documento? Essa ação não poderá ser desfeita!"
        dialogActions={
          <>
            <Button
              variant="outlined"
              color="primary"
              className={classes.outlinedWarningButton}
              onClick={() => setPendingRemoveItem(null)}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              className={classes.containerWarningButton}
              onClick={handleConfirmRemove}
            >
              Excluir
            </Button>
          </>
        }
      />
    </Box>
  )
}

LinkedDocumentList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object),
  onRemove: PropTypes.func,
  disabledInputs: PropTypes.bool,
  renderItemActions: PropTypes.func,
  emptyMessage: PropTypes.string,
  requiresConfirmation: PropTypes.func,
  showDocumentId: PropTypes.bool,
  showPendingLabel: PropTypes.bool,
}

export default LinkedDocumentList
